import SkeletonRow from "@/components/SkeletonRow";
import useScreen from "@/hooks/useScreen";
import { SysProgramStudi } from "@/types";
import { BookOpen, ChevronDown, GraduationCap, ScrollText } from "lucide-react";
import { useEffect, useState } from "react";

export default function ListProdi({
  dataProdi,
  isPending,
}: {
  dataProdi: {
    list_prodi_vokasi: SysProgramStudi[];
    list_prodi_sarjana: SysProgramStudi[];
    list_prodi: SysProgramStudi[];
  };
  isPending: boolean;
}) {
  const { isMobile } = useScreen();
  // Menggunakan boolean true/false agar lebih bersih daripada string kosong
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    // Auto collapse di mobile, auto open di desktop
    if (isMobile) setIsOpen(false);
    else setIsOpen(true);
  }, [isMobile]);

  // --- Helper Component: Header Section ---
  const SectionHeader = ({
    title,
    subtitle,
    icon: Icon,
    isOpen,
    onClick,
  }: any) => {
    return (
      <div
        className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group"
        onClick={onClick}
      >
        <div className="flex items-center gap-4">
          <div
            className={`p-2 rounded-full transition-colors ${
              isOpen
                ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                : "bg-slate-100 text-slate-500 dark:bg-slate-800 group-hover:bg-slate-200 dark:group-hover:bg-slate-700"
            }`}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-bold text-slate-800 dark:text-slate-100 text-base">
              {title}
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          </div>
        </div>
        <div
          className={`transition-transform duration-300 text-slate-400 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          <ChevronDown className="w-5 h-5" />
        </div>
      </div>
    );
  };

  // --- Helper Component: Prodi Card ---
  const ProdiCard = ({ prodi }: { prodi: SysProgramStudi }) => (
    <div className="group flex items-center gap-3 p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-all duration-200">
      <div className="flex-shrink-0 p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-slate-500 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 transition-colors">
        <BookOpen className="w-5 h-5" />
      </div>
      <div>
        <h3 className="font-semibold text-sm text-slate-900 dark:text-white leading-tight group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
          {prodi.unitkerja?.nama}
        </h3>
        {/* Jika ada data jenjang, bisa ditampilkan disini */}
        <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
          Program Studi
        </p>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 animate-in fade-in duration-500">
      <SectionHeader
        title="Program Profesi"
        subtitle="Program Studi Profesi"
        icon={GraduationCap}
        isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      />

      {isOpen && (
        <div className="animate-in slide-in-from-top-2 duration-300">
          {isPending ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
              {Array(6)
                .fill(null)
                .map((_, index) => (
                  <SkeletonRow key={index} />
                ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
              {dataProdi?.list_prodi_sarjana?.length > 0 ? (
                dataProdi.list_prodi_sarjana.map(
                  (sysProdi: SysProgramStudi) => (
                    <ProdiCard key={sysProdi.uk_id} prodi={sysProdi} />
                  ),
                )
              ) : (
                <div className="col-span-full flex flex-col items-center justify-center p-8 text-center bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                  <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-full mb-3 text-slate-400">
                    <ScrollText className="w-6 h-6" />
                  </div>
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                    Tidak ada program studi pascasarjana ditemukan.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
